import { FormLabel, InputField, SelectField } from '@/components/Form';
import {
  useFetchUserDataQuery,
  usePostUserDataMutation,
  useVerifyOTPMutation,
  useVerifyPhoneMutation,
} from '@/store/apis/userApi';
import {
  Avatar,
  Button,
  Col,
  Divider,
  Flex,
  Grid,
  Modal,
  Row,
  Tag,
  Typography,
  theme,
} from 'antd';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCountryOptions } from '@/hooks/useCountryOptions';
import { UserProfile } from '@/types';
import {
  displayError,
  displayErrorText,
  displaySuccess,
} from '@/utils/displayMessageUtils';
import { yupResolver } from '@hookform/resolvers/yup';
import { profileSchema } from '../../schema/profileSchema';
import { useFetchCountryByIPQuery } from '@/store/apis/coreApi';
import {
  CheckCircleFilled,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  PlusOutlined,
} from '@ant-design/icons';

import { OTPModal } from '@/components/Elements';
import PhoneInputField from '@/components/Form/PhoneInputField';
import {
  useSendTFNOTPMutation,
  useVerifyTFNOTPMutation,
  useVerifyTFNUpdateOTPMutation,
} from '../../api/profileAPI';
import TFNNumberEdit from './TFNNumberEdit';

const { useBreakpoint } = Grid;
const { useToken } = theme;

const Profile = () => {
  const { token } = useToken();
  const { data: profileData, refetch: refetchProfile } = useFetchUserDataQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [showTfnNumber, setShowTfnNumber] = useState(false);
  const [openOTPModal, setOpenOTPModal] = useState(false);
  const [openTFNOTPModal, setOpenTFNOTPModal] = useState(false);
  const [openTFNUpateOTPModal, setOpenTFNUpdateOTPModal] = useState(false);
  const [showTFNEditModal, setShowTFNEditModal] = useState(false);

  const [verifyPhone, { isLoading: verifyPhoneLoading }] =
    useVerifyPhoneMutation();
  const [verifyOTP] = useVerifyOTPMutation();

  const [sendTFNOTP, { isLoading: sendTFNOTPLoading }] =
    useSendTFNOTPMutation();
  const [verifyTFNOTPAndView] = useVerifyTFNOTPMutation();
  const [verifyTFNUpdateOTP] = useVerifyTFNUpdateOTPMutation();

  const { data: countryData } = useFetchCountryByIPQuery();

  const profileDetail = profileData?.data;
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(profileSchema),
  });

  useEffect(() => {
    if (countryData) {
      setValue('countryCode', countryData.data.country);
    }
  }, [countryData]);

  const [postUserData, { isLoading: postUserDataLoading }] =
    usePostUserDataMutation();

  const countryOptions = useCountryOptions();

  useEffect(() => {
    if (profileDetail) {
      setValue('firstName', profileDetail?.firstName ?? '');
      setValue('lastName', profileDetail?.lastName ?? '');
      setValue('countryCode', profileDetail?.country ?? '');
      setValue('email', profileDetail?.email ?? '');
      if (profileDetail?.hasTfnNumber) {
        setValue('hashTFNNumber', profileDetail?.hashedTfnNumber);
      }
      setValue('taxResidencyStatus', profileDetail?.taxResidencyStatus ?? '');
      setValue('phone', profileDetail?.phone ?? '');
    }
  }, [profileDetail]);

  const onSubmit = async (data: UserProfile) => {
    try {
      const filteredData = {
        phone: data?.phone ?? '',
        country: data?.countryCode ?? '',
        first_name: data?.firstName ?? '',
        last_name: data?.lastName ?? '',
        tax_residency_status: data?.taxResidencyStatus ?? '',
      };
      await postUserData(filteredData).unwrap();
      displaySuccess('Successfully Updated Profile.');
      refetchProfile();
    } catch (err) {
      displayError(err);
    }
  };

  const handleVerifyPhone = () => {
    verifyPhone()
      .unwrap()
      .then((res) => {
        displaySuccess(res.data.message);
        setOpenOTPModal(true);
      })
      .catch((err) => {
        displayError(err);
      });
  };

  const handleCancel = () => {
    setOpenOTPModal(false);
  };

  const handleVerify = async (otp: string) => {
    try {
      const response = await verifyOTP({ otp: otp }).unwrap();
      if (response.data.verification_status === 'pending') {
        displayErrorText('Invalid OTP. Please try again.');
      }
      if (response.data.verification_status === 'approved') {
        displaySuccess('Your phone number have been verified.');
        setOpenOTPModal(false);
        refetchProfile();
      }
    } catch (err) {
      displayErrorText('Invalid OTP. Please try again.');
    }
  };

  const handleViewTFNNumber = () => {
    sendTFNOTP()
      .unwrap()
      .then((res) => {
        displaySuccess(res.message);
        setOpenTFNOTPModal(true);
      })
      .catch((err) => {
        displayError(err);
      });
  };

  const handleTFNViewCancel = () => {
    setOpenTFNOTPModal(false);
  };

  const handleTFNOTPVerify = async (otp: string) => {
    try {
      const res = await verifyTFNOTPAndView({ code: otp }).unwrap();
      displaySuccess(res?.message);
      setOpenTFNOTPModal(false);
      setValue('hashTFNNumber', res.data.tfn_number);
      setShowTfnNumber(true);
    } catch (err) {
      displayError(err);
    }
  };
  const handleEditTFNNumber = () => {
    setShowTFNEditModal(true);
  };

  const handleTFNSumbit = () => {
    setShowTFNEditModal(false);
    setOpenTFNUpdateOTPModal(true);
  };

  const handleTFNUpdatCancel = () => {
    setOpenTFNUpdateOTPModal(false);
  };

  const handleTFNOTPUpdateVerify = async (otp: string) => {
    try {
      const res = await verifyTFNUpdateOTP({ code: otp }).unwrap();
      displaySuccess(res?.message);
      setOpenTFNUpdateOTPModal(false);
      setValue('hashTFNNumber', res?.data?.tfn_number);
      setShowTfnNumber(true);
      refetchProfile();
    } catch (err) {
      displayError(err);
    }
  };

  const { lg } = useBreakpoint();

  const initials =
    [profileDetail?.firstName, profileDetail?.lastName]
      .filter(Boolean)
      .map((n) => n![0].toUpperCase())
      .join('') || '?';

  return (
    <>
      <div style={{ width: '100%' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ── Profile header ── */}
          <Flex align="center" gap={16} style={{ marginBottom: 28 }}>
            <Avatar
              size={64}
              style={{
                backgroundColor: token.colorPrimary,
                fontSize: 24,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {initials}
            </Avatar>
            <div>
              <Typography.Title level={3} style={{ margin: 0 }}>
                {profileDetail?.firstName
                  ? `${profileDetail.firstName} ${profileDetail.lastName ?? ''}`
                  : 'Your Profile'}
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                Manage your personal information and security settings
              </Typography.Text>
            </div>
          </Flex>

          {/* ── Basic info ── */}
          <Typography.Text
            strong
            style={{
              fontSize: 13,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: token.colorTextSecondary,
            }}
          >
            Basic Information
          </Typography.Text>
          <Divider style={{ marginTop: 8, marginBottom: 20 }} />
          <Row gutter={[24, 4]}>
            <Col xs={24} md={12}>
              <InputField
                name="firstName"
                control={control}
                label="First Name"
                size="large"
                required
                error={errors?.firstName?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={12}>
              <InputField
                name="lastName"
                control={control}
                label="Last Name"
                size="large"
                required
                error={errors?.lastName?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={12}>
              <InputField
                disabled
                name="email"
                control={control}
                label="Email"
                size="large"
                required
                error={errors?.email?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={12}>
              <SelectField
                showSearch={true}
                options={countryOptions}
                name="countryCode"
                control={control}
                label="Country of Residence"
                size="large"
                placeholder="Select your country"
                error={errors.countryCode?.message ?? ''}
                required={true}
              />
            </Col>
          </Row>

          {/* ── Contact & verification ── */}
          <Typography.Text
            strong
            style={{
              fontSize: 13,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: token.colorTextSecondary,
              display: 'block',
              marginTop: 24,
            }}
          >
            Contact & Verification
          </Typography.Text>
          <Divider style={{ marginTop: 8, marginBottom: 20 }} />
          <Row gutter={[24, 4]}>
            <Col xs={24}>
              <div style={{ marginBottom: 24 }}>
                <FormLabel label="Phone Number" />
                <div
                  style={{
                    display: 'flex',
                    alignItems: lg ? 'center' : 'flex-start',
                    gap: 12,
                    flexDirection: lg ? 'row' : 'column',
                  }}
                >
                  <div style={{ flexGrow: 1, width: '100%' }}>
                    <PhoneInputField
                      name="phone"
                      control={control}
                      error={errors.phone?.message ?? ''}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Divider style={{ marginTop: 4, marginBottom: 20 }} />
          <Flex justify="flex-end">
            <Button
              loading={postUserDataLoading}
              type="primary"
              htmlType="submit"
              size="large"
              style={{ minWidth: 120 }}
            >
              Save changes
            </Button>
          </Flex>
        </form>

        {/* ── Tax information (separate from main form) ── */}
        <Typography.Text
          strong
          style={{
            fontSize: 13,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: token.colorTextSecondary,
            display: 'block',
            marginTop: 32,
          }}
        >
          Tax Information
        </Typography.Text>
        <Divider style={{ marginTop: 8, marginBottom: 20 }} />

        {profileDetail?.hasTfnNumber ? (
          <div
            style={{
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: 8,
              padding: '16px 20px',
              maxWidth: 480,
            }}
          >
            <Flex
              align="center"
              justify="space-between"
              style={{ marginBottom: 8 }}
            >
              <Flex align="center" gap={8}>
                <LockOutlined style={{ color: token.colorTextSecondary }} />
                <Typography.Text strong>TFN Number</Typography.Text>
              </Flex>
              <Tag
                icon={<CheckCircleFilled />}
                color="success"
                style={{ margin: 0 }}
              >
                Saved
              </Tag>
            </Flex>

            <Typography.Text
              style={{
                fontSize: 18,
                fontFamily: 'monospace',
                letterSpacing: '0.1em',
                display: 'block',
                marginBottom: 12,
              }}
            >
              {showTfnNumber
                ? watch('hashTFNNumber')
                : profileDetail?.hashedTfnNumber}
            </Typography.Text>

            <Flex gap={8}>
              {!showTfnNumber && (
                <Button
                  icon={<EyeOutlined />}
                  onClick={handleViewTFNNumber}
                  loading={sendTFNOTPLoading}
                >
                  View
                </Button>
              )}
              <Button icon={<EditOutlined />} onClick={handleEditTFNNumber}>
                Edit
              </Button>
            </Flex>

            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: 12 }}
            >
              Your TFN is stored securely and requires OTP verification to view
              or edit.
            </Typography.Text>
          </div>
        ) : (
          <div
            style={{
              border: `1px dashed ${token.colorBorderSecondary}`,
              borderRadius: 8,
              padding: '24px 20px',
              maxWidth: 480,
              textAlign: 'center',
            }}
          >
            <LockOutlined
              style={{
                fontSize: 28,
                color: token.colorTextSecondary,
                marginBottom: 8,
              }}
            />
            <Typography.Text
              style={{ display: 'block', marginBottom: 4 }}
              strong
            >
              No TFN Number on file
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 16, fontSize: 13 }}
            >
              Add your Tax File Number securely. You will need to verify via
              OTP.
            </Typography.Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleEditTFNNumber}
            >
              Add TFN Number
            </Button>
          </div>
        )}
      </div>
      <OTPModal
        visible={openOTPModal}
        onCancel={handleCancel}
        onVerify={handleVerify}
      />
      <OTPModal
        visible={openTFNOTPModal}
        onCancel={handleTFNViewCancel}
        onVerify={handleTFNOTPVerify}
        handleOtpResend={handleViewTFNNumber}
      />
      <Modal
        open={showTFNEditModal}
        onCancel={() => setShowTFNEditModal(false)}
        footer={null}
        title={
          profileDetail?.hasTfnNumber ? 'Edit TFN Number' : 'Add TFN Number'
        }
        width={400}
      >
        <TFNNumberEdit
          handleTFNSumbit={handleTFNSumbit}
          refetchProfile={refetchProfile}
        />
      </Modal>

      <OTPModal
        visible={openTFNUpateOTPModal}
        onCancel={handleTFNUpdatCancel}
        onVerify={handleTFNOTPUpdateVerify}
        resend={false}
      />
    </>
  );
};

export default Profile;
